import { Divider, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';

import Flex from '@Components/Bases/Flex';
import FormCheckbox from '@Components/Form/FormCheckBox';

import { StakeholderAnalysisFormValues } from '../ImpactsOnStakeholdersFormProvider/types';

import { formatToShortThaiDateTime } from '@Utils/datetime';
import { CompleteSectionFormValues } from '@Utils/Types/FormValuesTypes';
import { useProgressBar } from 'src/Constant/ProgressBarsContext/ProgressBar.Context';
import {
  checkAdditionalDescriptionEnRegex,
  checkAdditionalDescriptionThSpace,
  checkStakeholderGroupCompletionEn,
  checkStakeholderGroupCompletionTh,
  checkStkAnalysisAssetsValidEn,
  checkStkAnalysisAssetsValidTh,
  checkStkAnalysisIntroductionValidEn,
  checkStkAnalysisIntroductionValidTh,
  checkStkAnalysisRemarkEn,
  checkStkAnalysisRemarkTh,
} from './utils';
import { resetCompletedSection } from '@Utils/updateCompletedSection';

export enum OtherIdStakeholderAnalysis {
  OTHER_STAKEHOLDER = 144,
}

const StakeholderAnalysisValueChainFormCompletionCheckbox = () => {
  const { isPublished } = useProgressBar();
  const completedSection =
    (useWatch({
      name: 'stakeholderAnalysis.completedSection',
    }) as CompleteSectionFormValues) || {};
  const {
    isCompletedTh,
    isCompletedEn,
    dateTh,
    dateEn,
    roleStampTh,
    roleStampEn,
  } = completedSection;

  const formattedCompletedSectionDateTh = dateTh
    ? formatToShortThaiDateTime(dateTh)
    : '';
  const formattedCompletedSectionDateEn = dateEn
    ? formatToShortThaiDateTime(dateEn)
    : '';

  const {
    stakeholderGroups,
    remark,
    introduction,
    additionalDescriptionEn,
    additionalDescriptionTh,
  } = useWatch({
    name: 'stakeholderAnalysis',
  }) as StakeholderAnalysisFormValues;

  const stakeholderAnalysis = useWatch({
    name: 'stakeholderAnalysis',
  }) as StakeholderAnalysisFormValues;

  const { setValue } = useFormContext();

  const isCompleteTh =
    checkStakeholderGroupCompletionTh(stakeholderGroups) &&
    checkStkAnalysisAssetsValidTh(stakeholderAnalysis) &&
    checkStkAnalysisRemarkTh(remark) &&
    checkStkAnalysisIntroductionValidTh(introduction);
  const isCompleteEn =
    checkStakeholderGroupCompletionEn(stakeholderGroups) &&
    checkStkAnalysisRemarkEn(remark) &&
    checkStkAnalysisIntroductionValidEn(introduction) &&
    checkStkAnalysisAssetsValidEn(stakeholderAnalysis);

  const additionalDescriptionEnRegex = checkAdditionalDescriptionEnRegex(
    additionalDescriptionEn
  );

  const additionalDescriptionThSpace = checkAdditionalDescriptionThSpace(
    additionalDescriptionTh
  );

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    language: 'Th' | 'En'
  ) => {
    const updatedSection = { ...completedSection };

    if (language === 'Th') {
      updatedSection.isCompletedTh = e.target.checked;
    } else {
      updatedSection.isCompletedEn = e.target.checked;
    }

    resetCompletedSection(updatedSection, setValue, 'stakeholderAnalysis');
  };

  return (
    <>
      <Divider />
      <Flex gap={4} justifyContent={'center'} bgcolor={'grey.50'} py={1}>
        <Flex alignItems={'center'}>
          <Typography fontWeight="bold" textAlign={'right'}>
            ยืนยันการตรวจสอบข้อมูลครบถ้วน
          </Typography>
        </Flex>
        <Flex>
          <Flex flexDirection="column" alignItems={'center'}>
            <FormCheckbox
              name="stakeholderAnalysis.completedSection.isCompletedTh"
              label="จัดเตรียมข้อมูลเรียบร้อย (ภาษาไทย)"
              rules={{ required: 'กรุณากรอกข้อมูลให้ครบถ้วน' }}
              inputProps={{
                disabled:
                  isPublished || !isCompleteTh || !additionalDescriptionThSpace,
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleCheckboxChange(e, 'Th')
              }
            />
            {isCompletedTh && dateTh && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                โดย {roleStampTh} {formattedCompletedSectionDateTh}
              </Typography>
            )}
          </Flex>
          <Flex flexDirection="column" alignItems={'center'}>
            <FormCheckbox
              sx={{ ml: 2 }}
              name="stakeholderAnalysis.completedSection.isCompletedEn"
              label="จัดเตรียมข้อมูลเรียบร้อย (ภาษาอังกฤษ)"
              rules={{ required: 'กรุณากรอกข้อมูลให้ครบถ้วน' }}
              inputProps={{
                disabled:
                  isPublished || !isCompleteEn || !additionalDescriptionEnRegex,
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleCheckboxChange(e, 'En')
              }
            />
            {isCompletedEn && dateEn && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                โดย {roleStampEn} {formattedCompletedSectionDateEn}
              </Typography>
            )}
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

export default StakeholderAnalysisValueChainFormCompletionCheckbox;
